import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from "@angular/router";
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { AgGridModule } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { UsersService } from '../../service/entity-services/users.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';

import { EntityList } from '../../objects/entity-list';
import { User } from '../../objects/user';

import * as usersColumns from '../../../app/shared/entity-columns/users-columns';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule
  ]
})
export class UsersComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  public usersService = inject(UsersService);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  // snackBar is inherited from Entity base class

  @ViewChild('usersSidenavActions') usersSidenavActions!: TemplateRef<any>;

  // Observable for users
  allUsers$: Observable<User[]> = this.usersService.allUsers$;

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true; // For loading screen

  // Permissions - use signal from permissions store
  isViewer = this.permissionsStore.isViewer;

  // Column configuration
  defaultCols: any[] = [
    usersColumns.colUserNum,
    usersColumns.colUserFullName,
    usersColumns.colRole,
    usersColumns.colDepartments,
    usersColumns.colSites,
    usersColumns.colEmail
  ];

  selectedCols = this.defaultCols;

  // Column customization properties
  availableColumns: any[] = [];
  availableColumnSelectList: {value: any, viewValue: string}[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{value: any, viewValue: string}[]>;

  // AG-Grid default column configuration
  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  };
  public showFloatingFilters = false;

  // AG Grid configuration
  public frameworkComponents = {};

  constructor() {
    super();
    this.entityType = 'users'; // Set the entity type for view management
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    // Service will automatically load users

    this.titleService.setTitle('Users');

    // NOTE: View loading is now deferred to after grid is ready (see completeInitialization)

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Override onGridReady to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    // Call parent class onGridReady first
    super.onGridReady(params);

    // Complete initialization behind the loading screen
    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false; // Hide loading screen on error
    });
  }

  /**
   * Completes the grid initialization after grid is ready.
   * Uses official AG Grid APIs for state management.
   * Priority: Session Storage > Saved Views > Auto-size
   */
  private async completeInitialization(): Promise<void> {
    // CRITICAL: Suspend session tracking during initialization
    this.suspendSessionTracking = true;

    // PRIORITY 1: Check for session storage (temporary column adjustments)
    const hasSessionState = this.sessionStorageKey &&
      sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      // PRIORITY 2: Load saved views from backend
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView = this.savedNamedViews &&
        ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        // PRIORITY 3: No session, no saved view - auto-size
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    // Hide loading screen - must trigger change detection since we're outside Angular's zone
    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  /**
   * Builds the list of available columns
   */
  private buildAvailableColumns(): void {
    // Start with all Users columns
    this.availableColumns = [...usersColumns.allColumns];

    // Filter out non-column objects
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build and sort select list
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): {value: any, viewValue: string}[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option =>
      option.viewValue.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.usersSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Navigation
  createNewUser(): void {
    this.router.navigateByUrl('/securehome/new-user');
  }

  // Grid events
  onCellClicked(event: any): void {
    console.log('User clicked:', event.data);
    const userId = event.data.userNum || event.data.userId || event.data.email;
    if (userId) {
      this.router.navigate(['/securehome/user', userId]);
    } else {
      console.error('No user ID found in clicked user:', event.data);
    }
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  // Column management methods
  /**
   * Adds a column to the selected columns list
   */
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  /**
   * Removes a column from the selected columns list
   */
  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  /**
   * Applies the selected columns to the grid
   */
  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  /**
   * Opens the customize columns dialog
   */
  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState
        .filter((col: any) => !col.hide)
        .map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col =>
        visibleColIds.includes(col.colId)
      );
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  /**
   * Toggles the floating filter row on/off
   */
  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  // View management methods (saveView, saveNamedViewNow, loadNamedView,
  // openSavedReportsDialog, openDeleteSavedViewsDialog, showSaveNamedView)
  // are inherited from EntityList base class

  // Export functions with date formatting
  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'users_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'users_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}
